import type { Element, ElementContext, StepInfo } from '@/types/survey';
import type { Progression } from '@/types/respondent';

/**
 * Returns the id of the first step that does NOT have a non-deleted completion progression ("C").
 * If all steps are completed, returns null.
 *
 * @param stepMap - A map of step IDs to their StepInfo objects.
 * @param progressions - List of all progression objects.
 */
export function findFirstUncompletedStep(
  stepMap: Map<string, StepInfo>,
  progressions: Progression[]
): string | null {
  // If there are no steps in the survey, return null (nothing to complete)
  if (stepMap.size === 0) return null;

  for (const stepId of stepMap.keys()) {
    const isCompleted = progressions.some(
      (p) => p.step === stepId && p.type === 'C' && !p.deletedAt
    );
    if (!isCompleted) return stepId;
  }

  return null;
}

/**
 * Finds the page start index of the first element belonging to a given step.
 *
 * @param contextMap - Map of element IDs to their context metadata.
 * @param stepId - The ID of the step to search for.
 * @returns The pageStart index of the first element in the step, or -1 if not found.
 */
export function findPageStartOfFirstElementOnStep(
  contextMap: Map<string, ElementContext>,
  stepId: string
): number {
  for (const c of contextMap.values()) {
    if (c.step === stepId) {
      return c.pageStart;
    }
  }
  return -1;
}

/**
 * Finds the ElementContext corresponding to a given elementIndex.
 *
 * @param elementIndex - Map of element IDs to their context metadata.
 * @param elements - List of elements on the survey.
 * @param contextMap - A map of element IDs to their ElementContext object.
 * @returns An ElementContext object, or null if none is found for the given elementIndex.
 */
export function getElementContext(
  elementIndex: number,
  elements: Element[],
  contextMap: Map<string, ElementContext>,
): ElementContext | null {
  if (elementIndex < 0 || elementIndex >= elements.length) return null;

  const elementId: string = elements[elementIndex].id;
  return contextMap.get(elementId) ?? null
}

/**
 * Check whether a given element has any skip groups.
 *
 * @param element - The element to check
 * @returns boolean - Whether the element has at least one skip group.
 */
export function hasSkipGroups(element: Element): boolean {
  return 'skipGroups' in element && Array.isArray(element.skipGroups) && element.skipGroups.length > 0;
}